/* Built In Imports */
import { usePathname } from 'next/navigation';
import { useRef, useState } from 'react';

/* External Imports */
import {
  Box,
  Center,
  Flex,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import Slider from 'react-slick';

/* Internal Imports */
/* Components */
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import { ProgramTestimonialSt } from '@ieo/Common/CompLangConfig';
import { useIsMobile } from '@ieo/hooks/useIsMobile';

/* Services */
/* Styles */

/**
 *
 * @param {Object} sectionContent
 * @returns
 */
const SliderExperience = ({
  sectionContent,
  lang,
  color,
  paddingBottom,
  heightBase,
  arrowTop,
  left,
  right,
  region,
}) => {
  const [activeSlide, setActiveSlide] = useState(0);
  // console.log('slider', sectionContent);
  // console.log('ProgramTestimonialSt', ProgramTestimonialSt, ProgramTestimonialSt[lang].h1);
  const pathname = usePathname();
  const urlV3 = pathname === `/${region}/${lang}/inner-engineering-v3`;
  const url = pathname.includes('/rt-1');
  const sliderRef = useRef(null);
  const [modalData, setModalData] = useState(null);
  const { isMobile, isOpen, onOpen, onClose } = useIsMobile();

  const NextArrow = props => {
    const { onClick } = props;
    return (
      <Box
        bgColor={
          Math.ceil(activeSlide + 1) === sectionContent?.testimonials?.length
            ? '#ffffff'
            : '#F37021'
        }
        onClick={onClick}
        cursor="pointer"
        display="flex"
        alignItems="center"
        justifyContent="center"
        mt={{ base: '30px', md: '0px' }}
        pos={{ base: 'sticky', md: 'absolute' }}
        top={{
          base: arrowTop || '100%',
          md: '-90px',
          lg: '-102px',
          xl: '-102px',
        }}
        right={{
          base: right || '20%',
          sm: right || '30%',
          md: '40px',
          xl: '-70px',
          '2xl': '-80px',
        }}
        left={{ base: '70%', sm: '60%', md: 'auto' }}
        zIndex="99"
        w={{ base: '44px', md: '64px' }}
        h={{ base: '44px', md: '64px' }}
        _hover={{
          boxShadow: '0 4px 10px -5px #6D6358',
          bgColor:
            Math.ceil(activeSlide + 1) === sectionContent?.testimonials?.length
              ? '#ffffff'
              : '#BC570F',
        }}
      >
        <svg
          width="10"
          height="14"
          viewBox="0 0 10 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.83337 11.3333L7.16671 6.99996L2.83337 2.66663"
            stroke={
              Math.ceil(activeSlide + 1) ===
              sectionContent?.testimonials?.length
                ? '#F37021'
                : '#ffffff'
            }
            strokeWidth="3"
            strokeLinecap="square"
          />
        </svg>
      </Box>
    );
  };

  const PrevArrow = props => {
    const { onClick } = props;
    return (
      <Box
        cursor="pointer"
        bgColor={
          Math.ceil(activeSlide + 1) === sectionContent?.testimonials?.length
            ? '#F37021'
            : '#ffffff'
        }
        onClick={onClick}
        display="flex"
        alignItems="center"
        justifyContent="center"
        pos={{ base: 'absolute', md: 'absolute' }}
        top={{
          base: arrowTop || '100%',
          md: '-90px',
          lg: '-102px',
          xl: '-102px',
        }}
        right={{ base: '0', md: '174px', xl: '175px' }}
        left={{
          base: left || '20%',
          sm: left || '30%',
          md: '75.5%',
          lg: '81.4%',
          xl: '93%',
          '2xl': '94%',
        }}
        zIndex="99"
        w={{ base: '44px', md: '64px' }}
        h={{ base: '44px', md: '64px' }}
        _hover={{
          boxShadow: '0 4px 10px -5px #6D6358',
          bgColor:
            Math.ceil(activeSlide + 1) === sectionContent?.testimonials?.length
              ? '#BC570F'
              : '#ffffff',
        }}
        bottom={{ base: '0', md: 'inherit' }}
      >
        <svg
          width="10"
          height="14"
          viewBox="0 0 10 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.16663 11.6678L2.83329 7.33443L7.16663 3.0011"
            stroke={
              Math.ceil(activeSlide + 1) ===
              sectionContent?.testimonials?.length
                ? '#ffffff'
                : '#F37021'
            }
            strokeWidth="3"
            strokeLinecap="square"
          />
        </svg>
      </Box>
    );
  };

  const settings = {
    arrow: true,
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: url ? 2 : 1.5,
    slidesToScroll: 1,
    initialSlide: 0,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (current, next) => setActiveSlide(next),
    responsive: [
      {
        breakpoint: 1441,
        settings: {
          slidesToShow: url ? 2 : 1.5,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1201,
        settings: {
          slidesToShow: url ? 2 : 1.5,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: url ? 1 : 1.3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          initialSlide: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <Flex
      maxW="1330px"
      flexDir="column"
      alignItems="center"
      w="full"
      justify="center"
      mb={{ md: '100px', xl: paddingBottom || '146px' }}
    >
      <Box
        maxW={1030}
        w="full"
        pos="relative"
        py="20px"
        px={{ base: '16px', xl: 0 }}
      >
        <Flex
          justifyContent="space-between"
          w={{ base: 'full', md: '87.5%', lg: '79.6%', xl: '90%' }}
          alignItems={{ md: 'baseline', lg: 'center' }}
          minH={{ base: '60px', md: '150px' }}
        >
          <Heading
            as="h2"
            pt={{ base: '20px', md: 0 }}
            pb={{ base: '16px', md: '10px' }}
            pr={{ md: '14%', lg: '6%', xl: '2%' }}
            textAlign="left"
            color={color || '#12166F'}
            letterSpacing={{ base: '-1px', md: '-0.66px' }}
            lineHeight={{ base: '29.96px', md: '42.68px' }}
            {...ProgramTestimonialSt[lang].h2}
            w={{ base: 'full', md: 'auto' }}
            dir={lang == 'ar' ? 'rtl' : 'ltr'}
          >
            {' '}
            {sectionContent?.title?.[0]?.titleText}
          </Heading>
          <Text
            pos={{ base: 'absolute', md: 'static' }}
            bottom={{ base: '30px', md: '155px' }}
            left="47%"
            color={color || '#433F3C'}
            {...ProgramTestimonialSt[lang].countNumber}
            display={{ base: urlV3 ? 'none' : 'block', md: 'block' }}
          >
            {Math.ceil(activeSlide + 1)}/{sectionContent?.testimonials?.length}
          </Text>
        </Flex>
        <Slider {...settings} className={url && 'slider-width'}>
          {sectionContent?.testimonials?.map((item, index) => (
            <Box
              w="full"
              key={index}
              pb={{ base: '0px', xl: paddingBottom || '0px' }}
            >
              <Box
                position="relative"
                display="flex"
                gridGap="30px"
                css={{ '-webkit-grid-gap': '30px' }}
                w={{ base: '90%', lg: 'full' }}
                mx="auto"
              >
                <Image
                  src={item?.photo?.url}
                  alt={item?.photo?.alt || ''}
                  title={item?.photo?.title || ''}
                  mt={{ base: '12px', md: '30px' }}
                  w={{ base: '101px', md: '256px' }}
                  h={{ base: '131px', md: '328px' }}
                  pos="absolute"
                />

                <Flex
                  bg="#fff"
                  flexDirection="column"
                  m={{
                    base: '0 0px 0 20px',
                    md: '0 40px 0 100px',
                    lg: '0 40px 0 100px',
                  }}
                  p={{ base: '20px', md: '0px 40px 40px 196px' }}
                  flexGrow="1"
                  boxShadow="0 12px 14px -15px #6D6358"
                  overflow="auto"
                  // height={{
                  //   base: heightBase || '430px',
                  //   md:
                  //     lang === 'ar'
                  //       ? heightBase || '420px'
                  //       : heightBase || '540px',
                  //   lg:
                  //     lang === 'ar'
                  //       ? heightBase || '420px'
                  //       : heightBase || '500px',
                  //   xl: heightBase || 'auto',
                  // }}
                  minH={{ base: '400px', xl: '424px' }}
                >
                  <Box
                    ml={{ base: '80px', md: 0 }}
                    minH="120px"
                    h="auto"
                    dir={lang == 'ar' ? 'rtl' : 'ltr'}
                  >
                    <StructuredTextParser
                      str={render(item?.testimonialTitle)}
                      {...ProgramTestimonialSt[lang].p}
                      color="#6D6358"
                      pt={{ base: '10px', md: '40px', lg: '70px' }}
                      dir={lang == 'ar' ? 'rtl' : 'ltr'}
                    />

                    <Text
                      // pt={{ base: '10px', md: '40px', lg: '70px' }}
                      color="#6D6358"
                      {...ProgramTestimonialSt[lang].title}
                      dir={lang == 'ar' ? 'rtl' : 'ltr'}
                    >
                      {' '}
                      {item?.userName}
                    </Text>

                    <StructuredTextParser
                      mt="0px"
                      str={render(item?.userDescription)}
                      {...ProgramTestimonialSt[lang].subTitle}
                      maxW="auto"
                      color="#12166F"
                      dir={lang == 'ar' ? 'rtl' : 'ltr'}
                    />
                  </Box>

                  <StructuredTextParser
                    str={render(item?.testimonialText)}
                    {...ProgramTestimonialSt[lang].p}
                    color="#6D6358"
                    dir={lang == 'ar' ? 'rtl' : 'ltr'}
                  />
                </Flex>
              </Box>
            </Box>
          ))}
        </Slider>
      </Box>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        isCentered={true}
        h={{ md: '424px', base: '100%' }}
        finalFocusRef={sliderRef}
      >
        <ModalOverlay bg="blackAlpha.400" />
        <ModalContent background="none" my={{ base: '0', md: '0', lg: '0' }}>
          <ModalBody
            p={{
              base: '25px 0 0 0',
              md: '0 0 0 30px',
              lg: '0 0 0 10px',
              xl: '0px',
            }}
          >
            <Center
              pb={{ base: '0%', md: '10%' }}
              w={{ base: '100%', md: '632px', lg: '694px' }}
              mx="auto"
              height="100%"
              m={{ base: '15% 0 0 0', md: '10% 0 0' }}
              pos="relative"
              bg={url ? '#FDFBF7' : '#F1EBDF'}
              maxH={{
                base: '100%',
                md: '446px',
                lg: '424px',
              }}
            >
              <Box
                position="absolute"
                top={{
                  base: '17px',
                  md: '21px',
                  lg: '21px',
                }}
                right={{
                  base: '22px',
                  md: '24px',
                  lg: '15px',
                }}
                onClick={onClose}
                w="auto"
                height="auto"
                cursor="pointer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M19 20.4143L12 13.4143L4.99997 20.4143L3.58576 19.0001L10.5858 12.0001L3.58576 5.00009L4.99997 3.58588L12 10.5859L19 3.58588L20.4142 5.00009L13.4142 12.0001L20.4142 19.0001L19 20.4143Z"
                    fill="#E86F34"
                  />
                </svg>
              </Box>
              <Flex
                h="100%"
                w={{ base: '100%', md: '90%', lg: '100%' }}
                flexDir={{ base: 'column', md: 'row' }}
                pl={{ base: '35px', md: '0' }}
                pr={{ base: '23px', md: '0' }}
                mb={{ base: '30px', md: '0' }}
                pt={{ base: '20px', md: '30px' }}
              >
                <Box
                  w={{
                    base: '167px',
                    md: '239px',
                    lg: '239px',
                  }}
                  maxW="none"
                  // h={{base:"auto",md:"310px",lg:"269px"}}
                >
                  <Image
                    src={modalData?.photo?.url}
                    alt={modalData?.photo?.alt || ''}
                    mt={{
                      base: '-64px',
                      md: '60px',
                      lg: '70px',
                    }}
                    ml={{ base: '10px', md: '-55px' }}
                    h={{ base: 'auto', md: '302px' }}
                    w={{ base: '100%', md: '226.5px' }}
                    maxW="none"
                    objectPosition="top"
                  />
                </Box>
                <Box
                  p={{
                    base: '0px',
                    md: '20px 20px 0 20px',
                    lg: '20px',
                  }}
                >
                  <Box
                    mt={{
                      base: '20px',
                      md: '60px',
                      lg: '80px',
                    }}
                    w="100%"
                    maxH={330}
                    h="100%"
                    pl={{ md: '30px', lg: '50px' }}
                    pr={{ md: '26px', lg: '30px' }}
                    className="stories-scroll"
                    overflowY="auto"
                  >
                    <Box
                      ml={{ base: '80px', md: 0 }}
                      minH="120px"
                      h="auto"
                      dir={lang == 'ar' ? 'rtl' : 'ltr'}
                    >
                      <Text
                        dir={lang == 'ar' ? 'rtl' : 'ltr'}
                        color="#433F3C"
                        fontSize="16px"
                        lineHeight="22.68px"
                        fontFamily="FedraSansStd-medium"
                      >
                        {' '}
                        {modalData?.userName}
                      </Text>
                    </Box>
                    <StructuredTextParser
                      str={render(modalData?.testimonialText.value)}
                      // {...TestimonalSt[lang].modalDes}
                      color="#423E3B"
                      mt="0px"
                      className="textp"
                    />
                    <Box
                      pt={{ base: '10px', md: '40px' }}
                      fontFamily="FedraSansStd-book"
                    >
                      <StructuredTextParser
                        mt="0px"
                        str={render(modalData?.userDescription)}
                        color="#6D6358"
                        {...ProgramTestimonialSt[lang].title}
                        fontSize={{ base: '16px', md: '18px' }}
                        lineHeight={{ base: '20.16px', md: '22.68px' }}
                        dir={lang == 'ar' ? 'rtl' : 'ltr'}
                      />

                      <StructuredTextParser
                        str={render(modalData?.additionalDetails)}
                        mt="0px"
                        {...ProgramTestimonialSt[lang].subTitle}
                        fontSize={'16px'}
                        lineHeight={'20.16px'}
                        maxW="auto"
                        color="#12166F"
                      />
                    </Box>
                  </Box>
                </Box>
              </Flex>
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default SliderExperience;
